import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { enablePush } from "@/lib/push";
import { playAlertChime, soundEnabled } from "@/lib/sound";
import { StatusBadge } from "@/components/common";
import { Bell, BellRing, Check } from "lucide-react";

export function NotificationCenter() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [enabling, setEnabling] = useState(false);
  const [filter, setFilter] = useState("all");
  const ref = useRef(null);
  const lastSeen = useRef(null);

  const kindOf = (n) => {
    if (n.tag === "aurum-level" || (n.status || "").startsWith("LEVEL")) return "level";
    const s = n.status || "";
    if (s.includes("ENTRY") || s.includes("SETUP")) return "entry";
    return "other";
  };

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      const notifs = data.notifications || [];
      setItems(notifs);
      setUnread(data.unread || 0);
      // Sound + on-screen alert for newly-arrived alerts while logged in.
      if (notifs.length) {
        const newest = notifs[0].created_at; // sorted desc
        if (lastSeen.current === null) {
          lastSeen.current = newest; // baseline — don't announce history
        } else if (newest > lastSeen.current) {
          const fresh = notifs.filter((n) => n.created_at > lastSeen.current);
          lastSeen.current = newest;
          const alert = fresh.find((n) => n.status === "ENTRY CONDITIONS SATISFIED") || fresh[0];
          if (alert) {
            if (soundEnabled()) playAlertChime();
            toast(alert.title?.replace("AURUM · ", ""), {
              description: alert.body, duration: 9000,
            });
          }
        }
      }
    } catch {}
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const openPanel = async () => {
    setOpen(!open);
    if (!open && unread > 0) {
      await api.post("/notifications/read").catch(() => {});
      setUnread(0);
    }
  };

  const turnOn = async () => {
    setEnabling(true);
    const res = await enablePush();
    setEnabling(false);
    if (res.status === "enabled") toast.success("Browser alerts enabled.");
    else if (res.status === "denied") toast.error("Notifications blocked. Enable them in browser settings.");
    else toast.info("Browser push is unavailable here (in-app alerts still work).");
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={openPanel} data-testid="notif-bell"
        className="relative p-2 rounded-lg text-[var(--text-sec)] hover:text-[var(--text)] hover:bg-[var(--surface)] transition-colors" aria-label="Notifications">
        {unread > 0 ? <BellRing size={18} className="text-aurum" /> : <Bell size={18} />}
        {unread > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-critical" data-testid="notif-badge" />}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-80 rounded-xl border border-[var(--border2)] bg-[var(--card2)] shadow-2xl z-50" data-testid="notif-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border2)]">
            <span className="text-sm font-semibold">Alerts</span>
            <button onClick={turnOn} disabled={enabling} data-testid="enable-push"
              className="text-[11px] rounded-full px-2.5 py-1 border border-aurum text-aurum hover:bg-[rgba(214,174,87,0.1)] transition-colors">
              {enabling ? "Enabling…" : "Enable browser push"}
            </button>
          </div>
          <div className="flex items-center gap-1 px-3 py-2 border-b border-[var(--border2)]">
            {[["all", "All"], ["entry", "Entry"], ["level", "Levels"]].map(([k, lbl]) => {
              const count = k === "all" ? items.length : items.filter((n) => kindOf(n) === k).length;
              return (
                <button key={k} onClick={() => setFilter(k)} data-testid={`notif-filter-${k}`}
                  className={`text-[11px] rounded-full px-2.5 py-1 transition-colors ${filter === k ? "bg-aurum text-black font-semibold" : "text-[var(--text-sec)] hover:bg-[var(--surface)]"}`}>
                  {lbl}{count > 0 && <span className="ml-1 opacity-70">{count}</span>}
                </button>
              );
            })}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {(() => {
              const shown = filter === "all" ? items : items.filter((n) => kindOf(n) === filter);
              return shown.length === 0 ? (
              <div className="px-4 py-8 text-center text-xs text-[var(--text-muted)]">
                {items.length === 0
                  ? "No alerts yet. Aurum will notify you when your conditions appear."
                  : `No ${filter} alerts today.`}
              </div>
            ) : shown.map((n, i) => (
              <div key={i} className="px-4 py-3 border-b border-[var(--border-subtle)]" data-testid={`notif-item-${kindOf(n)}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-[var(--text)]">{n.title?.replace("AURUM · ", "")}</span>
                  {n.status && <StatusBadge status={n.status} />}
                </div>
                <p className="mt-1 text-[11px] text-[var(--text-sec)]">{n.body}</p>
                <div className="mt-1 text-[10px] text-[var(--text-muted)]">{new Date(n.created_at).toLocaleString()}</div>
              </div>
            ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
